<template>
  <div>
    <add-or-update-member-profile-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />
    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :loading="loading"
      :error-message="errorMessage"
      :empty-text="emptyText"
      :show-search="true"
      :show-filter-by-default="true"
      :show-import-button="can('csv_upload_data:CanImport')"
      import-type="bio_data"
      @import="onImport"
      @row-clicked="onView"
    >
      <template #filters>
        <SearchFilter @search="fetchAllData" />
      </template>

      <template #cell(member)="data">
        <b-media vertical-align="center">
          <template #aside>
            <b-avatar
              size="32"
              :src="getValueFromSource(data, 'item.avatar.path')"
              :text="sentenceCase(avatarText(`${data.item.full_name}`))"
              :variant="`light-primary`"
              :to="viewRoute(data.item)"
            />
          </template>
          <b-link
            :to="viewRoute(data.item)"
            class="font-weight-bold d-block text-nowrap"
          >
            {{ sentenceCase(getValueFromSource(data, 'item.full_name')) }}
          </b-link>
          <span class="text-muted">{{ data.item.email }}</span><br />
        </b-media>
      </template>

      <template #cell(efile)="data">
        <b-badge
          v-if="efileIndicators[data.item._id]"
          pill
          variant="light-primary"
          :title="`${efileIndicators[data.item._id].count} document(s)`"
        >
          {{ efileIndicators[data.item._id].count }} doc{{ efileIndicators[data.item._id].count === 1 ? "" : "s" }}
        </b-badge>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell(region)="data">
        {{ getGeoData(data.item, 'region') }}
      </template>

      <template #cell(area)="data">
        {{ getGeoData(data.item, 'area') }}
      </template>

      <template #cell(district)="data">
        {{ getGeoData(data.item, 'district') }}
      </template>

      <template #cell(assembly)="data">
        {{ getGeoData(data.item, 'assembly') }}
      </template>

      <template v-if="can('csv_upload_data:CanList')" #action_button>
        <b-button :to="{ name: 'import-reports' }" variant="primary" class="d-flex align-items-center" style="gap: 5px;">
          <feather-icon icon="ClipboardIcon" />
          View Import Report
        </b-button>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item :to="viewRoute(data.item)">
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">View</span>
          </b-dropdown-item>
          <b-dropdown-item :to="editRoute(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="ministersOnly ? can('ministers:CanDelete') : can('member_profiles:CanDelete')" @click="() => onDeleteProfile(data.item)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>
  
<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import SearchFilter from '@/@core/components/query-builder/search-filter.vue';
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateMemberProfileModal from "@/@core/components/shared/church/AddOrUpdateMemberProfileModal.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    SearchFilter,
    BDropdownItem,
    AddOrUpdateMemberProfileModal
  },
  directives: {
    Ripple,
  },
  props: {
    ministersOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      records: [],
      efileIndicators: {},
      errorMessage: "",
      appliedFilters: null,

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'member', label: 'Member', sortable: false, },
        { key: 'member_type.title', label: 'Member Type', sortable: false, },
        { key: 'member_title.title', label: 'Title', sortable: false, },
        { key: 'member_designation.designation', label: 'Designation', sortable: false, },
        { key: 'region', label: "Region", sortable: false, },
        { key: 'area', label: "Area", sortable: false, formatter: val => val || '-' },
        { key: 'district', label: "District", sortable: false, formatter: val => val || '-' },
        { key: 'assembly', label: "Assembly", sortable: false, formatter: val => val || '-' },
        { key: 'efile', label: 'E-file', sortable: false },
        { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
    // An empty table is ambiguous on its own: it could mean the search excluded
    // everything, or that the server genuinely returned nothing. Say which.
    emptyText() {
      if (this.hasActiveQuery) {
        return "No records match your search or filters.";
      }
      return `The server returned no ${this.ministersOnly ? 'minister' : 'member'} records.`;
    },
    hasActiveQuery() {
      const hasFilters = this.getValueFromSource(this.appliedFilters, 'filters', []).length > 0;
      return Boolean(this.search) || hasFilters;
    },
    perPageOptions() {
      return this.$store.getters[`navigation/recordsPerPageOptions`];
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
    ministersOnly: watchHandlerRequiresPageReset,
  },
  created() {
    // the E-file column is only for people who may see electronic files
    if (!this.can("member_efile:CanView")) {
      this.tableColumns = this.tableColumns.filter((c) => c.key !== "efile");
    }
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    // The URL is user-editable and older links can carry a page size this app
    // never offers (or no number at all). An out-of-range limit reaches the API
    // as `NaN`/`1000` and comes back as a failed or punishingly slow request.
    const requestedLimit = Number(limit);
    const requestedPage = Number(page);

    this.search = search || "";
    this.perPage = this.perPageOptions.includes(requestedLimit) ? requestedLimit : this.perPageOptions[0];
    this.currentPage = Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1;

    // Initial load must not depend on a watcher happening to fire
    this.debounceFetchAllData();
  },
  methods: {
    // P19: document counts come from PAC's own cache, so this never waits on the file provider.
    // A failure here only means the column stays empty.
    async loadEfileIndicators(rows) {
      this.efileIndicators = {};
      if (!this.can("member_efile:CanView") || !rows.length) return;
      try {
        const { data } = await this.useJwt().mainService.memberEfile.indicators(rows.map((r) => r._id));
        this.efileIndicators = data.data || {};
      } catch (error) {
        this.efileIndicators = {};
      }
    },
    async fetchAllData(filters = {}) {
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }

        if (typeof this.ministersOnly === "boolean") {
          query.ministers_only = this.ministersOnly;
        }

        if (filters && filters.filter_mode && filters.filters) {
          query.filters = JSON.stringify(filters);
        }

        this.appliedFilters = filters;

        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.memberProfiles.list(query);
        const { data, pagination } = request.data;

        // A response that parses but carries no `data` array used to leave the
        // table silently empty, which looks identical to "there are no records".
        // Treat a missing or malformed payload as the failure it is.
        if (!Array.isArray(data)) {
          throw new Error("The server did not return a list of records. Please contact support if this persists.");
        }

        this.totalRecords = this.getValueFromSource(pagination, 'totalRecords', 0);
        this.perPage = this.getValueFromSource(pagination, 'limit', this.perPage);
        this.records = data;
        this.loadEfileIndicators(data);
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        // Surfaced in-page as well as in a toast: the toast dismisses itself and
        // is easy to miss, which is how a failing list reads as an empty one.
        this.errorMessage = error_message;
        this.records = [];
        this.totalRecords = 0;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    viewRoute(data) {
      if (!data || !data._id) return {};
      const name = this.ministersOnly ? "ministers" : "members"
      const route = { name: `${name}-view`, params: { id: data._id } }
      return route
    },
    editRoute(data) {
      if (!data || !data._id) return {};
      const name = this.ministersOnly ? "ministers" : "members"
      const route = { name: `${name}-detail`, params: { id: data._id } }
      return route
    },
    onView(data) {
      this.$router.push(this.viewRoute(data));
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null
      }
    },
    async onImport(file) {
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append("ministers_bio", file);

        const response = await this.useJwt().mainService.users.importBioData(formData);
        this.importReport = this.getValueFromSource(response, 'data.data');

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: "User records import has been processed successfully."
          },
        });
        this.$router.push({
          name: 'import-reports-detail',
          params: { id: this.importReport.id }
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          }
        });
      } finally {
        this.loading = false;
      }
    },
    async onDeleteProfile(profile) {
      try {
        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this profile and related user data?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.value) return;

        this.loading = true;
        await this.useJwt().mainService.memberProfiles.delete(profile._id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Deleted!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Profile and related user data deleted successfully',
          },
        });
        this.fetchAllData();
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>
  

  
